import type { ComponentPropsWithRef, ReactElement, ReactNode } from 'react';
import { IconButton, type IconButtonProps } from '../candidates/icon-button.tsx';
import { ValueChip, ValueChipBody, ValueChipLabel, ValueChipRemove } from '../primitives/value-chip.tsx';
import { cn } from '../util/classes.ts';
import { Overflow } from '../util/overflow.tsx';
import { useBreakpoint } from '../util/use-breakpoint.tsx';

export type PickerValueProps = ComponentPropsWithRef<'div'>;

/**
 * The row a field shows its picked value in: `PickerValueChip` for what is set —
 * inside `PickerValueChips` where there are several — `PickerValueSuggestion` for
 * what isn't, and one `PickerValueAction` to open the picker.
 */
export function PickerValue({ className, ...props }: PickerValueProps): ReactElement {
    return (
        <div
            data-slot="picker-value"
            className={cn('flex min-w-0 items-center gap-cozy', className)}
            {...props}
        />
    );
}

export type PickerValueChipProps = Omit<ComponentPropsWithRef<'span'>, 'onClick'> & {
    /** The chip's text, and the accessible name of its body. */
    label: string;
    /** Opens the picker to change this value. */
    onOpen?: () => void;
    /** Clears this value. Its presence is what makes the chip removable. */
    onRemove?: () => void;
    /** Names the remove action, e.g. `„Sabine Falk“ entfernen`. Defaults to `label`. */
    removeLabel?: string;
};

/** A value that is set. Children are its leading icon, where the value has one. */
export function PickerValueChip({
    label,
    onOpen,
    onRemove,
    removeLabel,
    children,
    ...props
}: PickerValueChipProps): ReactElement {
    return (
        <ValueChip data-slot="picker-value-chip" variant="value" label={label} {...props}>
            <ValueChipBody onClick={onOpen}>
                {children}
                <ValueChipLabel />
            </ValueChipBody>
            {onRemove !== undefined && <ValueChipRemove label={removeLabel ?? label} onClick={onRemove} />}
        </ValueChip>
    );
}

export type PickerValueChipsProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    children: ReactNode;
    /** Names the chip standing in for the ones that didn't fit, e.g. `+2 weitere`. */
    overflowLabel: (hiddenCount: number) => string;
    /** Opens the picker from that overflow chip. */
    onOpen?: () => void;
};

/** Collapses the chips past the second — the first only, below `md` — into a count. */
export function PickerValueChips({ overflowLabel, onOpen, ...props }: PickerValueChipsProps): ReactElement {
    const maxVisible = useBreakpoint('md') ? 2 : 1;

    return (
        <Overflow
            data-slot="picker-value-chips"
            maxVisible={maxVisible}
            renderOverflow={(hiddenCount) => (
                <ValueChip
                    data-slot="picker-value-overflow"
                    variant="value"
                    label={overflowLabel(hiddenCount)}
                    className="shrink-0"
                >
                    <ValueChipBody onClick={onOpen}>
                        <ValueChipLabel />
                    </ValueChipBody>
                </ValueChip>
            )}
            {...props}
        />
    );
}

export type PickerValueSuggestionProps = Omit<ComponentPropsWithRef<'span'>, 'onClick'> & {
    /** The chip's text, and the accessible name of its body. */
    label: string;
    /** What accepting it does — set that value, or open the picker. */
    onClick?: () => void;
};

/**
 * The dashed chip an empty field shows: a value it offers to set, or the
 * placeholder naming what the field is for.
 */
export function PickerValueSuggestion({
    label,
    onClick,
    children,
    ...props
}: PickerValueSuggestionProps): ReactElement {
    return (
        <ValueChip data-slot="picker-value-suggestion" variant="suggestion" label={label} {...props}>
            <ValueChipBody onClick={onClick}>
                {children}
                <ValueChipLabel />
            </ValueChipBody>
        </ValueChip>
    );
}

export type PickerValueActionProps = IconButtonProps;

/** The trailing action that opens the picker. `secondary` unless told otherwise. */
export function PickerValueAction(props: PickerValueActionProps): ReactElement {
    return <IconButton data-slot="picker-value-action" variant="secondary" {...props} />;
}
